import {
  modelCatalogueOf,
  panoVariantsOf,
  servedWorldsOf,
  withCurrentWorldNames,
  type Lesson,
} from '@vrlearning/lesson-schema';
import { catalogueOfServed } from './api/served-catalogue.js';
import { browserSocket, type Assignment, type LiveState } from './api/session-client.js';

/** What the teacher's window onto a class is handed: the lesson, never anything to prefetch. */
export type ViewedAssignment = Pick<
  Assignment,
  'sessionId' | 'lesson' | 'media' | 'cues' | 'variants' | 'models' | 'worlds'
>;

/**
 * `session.assigned` as the teacher's window reads it: the lesson, its signed media and its 360°
 * photos' cubes (ADR-041; the console's preview iframe takes the cube on an Apple Silicon Mac, the
 * JPEG elsewhere, as a headset would). Nothing without a lesson or a session.
 */
export function viewedAssignmentOf(payload: Record<string, unknown>): ViewedAssignment | undefined {
  const lesson = payload['lesson'];
  const sessionId = payload['sessionId'];
  if (!lesson || typeof sessionId !== 'string') return undefined;
  const worlds = servedWorldsOf(payload['worlds']);
  return {
    sessionId,
    // A world's old names shown as its current ones, as the class's headsets play them (spec 2026-10-16).
    lesson: currentNames(lesson as Lesson, worlds),
    media: (payload['media'] as Record<string, string>) ?? {},
    variants: panoVariantsOf(payload['variants']),
    // The library models it names (ADR-043): seen in the teacher's window as in a headset.
    models: modelCatalogueOf(payload['models']),
    // The served world packages it names (ADR-049): the same world as the class's.
    worlds,
    // The teacher's window onto the class stays text (ADR-035): silent, no subtitles.
    cues: {},
  };
}

/** The lesson with its world's old names as the current ones; as it came when it cannot be read so. */
function currentNames(lesson: Lesson, worlds: ReturnType<typeof servedWorldsOf>): Lesson {
  try {
    return withCurrentWorldNames(lesson, catalogueOfServed(worlds));
  } catch {
    return lesson;
  }
}

/**
 * What the class is looking at, for the teacher's screen.
 *
 * The console embeds this in an iframe so a teacher can talk a class through a
 * step while seeing the step. It is the same engine the headsets run, which is
 * the point: a second renderer written to approximate the first would drift
 * from it, and a teacher describing something the class cannot see is worse
 * than no preview at all.
 *
 * Read-only by construction. It opens the socket as the teacher already is,
 * renders what arrives, and sends nothing: there is no input wired to it and
 * no result queue behind it.
 *
 * Authenticated by a single-use ticket in the URL, which is the same
 * credential and the same 30-second life as `/v1/ws` itself. It carries no
 * bearer token, which is why the API sends a teacher the lesson body over the
 * socket rather than expecting a fetch.
 */
export type PreviewHandle = {
  /** Called when the lesson arrives and whenever the class moves. */
  onAssigned(handler: (assignment: ViewedAssignment) => void): void;
  onState(handler: (state: LiveState) => void): void;
  onEnded(handler: () => void): void;
  onTrouble(handler: (message: string) => void): void;
  stop(): void;
};

type Frame = { type?: string; payload?: Record<string, unknown> };

/** Reads `?preview=<sessionId>&ticket=<t>` off the page's own URL. */
export function previewRequest(
  href: string = globalThis.location?.href ?? '',
): { sessionId: string; ticket: string } | undefined {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return undefined;
  }

  const sessionId = url.searchParams.get('preview');
  const ticket = url.searchParams.get('ticket');
  return sessionId && ticket ? { sessionId, ticket } : undefined;
}

export function startPreview(request: { sessionId: string; ticket: string }): PreviewHandle {
  let onAssigned: ((assignment: ViewedAssignment) => void) | undefined;
  let onState: ((state: LiveState) => void) | undefined;
  let onEnded: (() => void) | undefined;
  let onTrouble: ((message: string) => void) | undefined;

  const scheme = globalThis.location?.protocol === 'https:' ? 'wss' : 'ws';
  const host = globalThis.location?.host ?? 'localhost';
  const url =
    `${scheme}://${host}/v1/ws?ticket=${encodeURIComponent(request.ticket)}` +
    `&session=${encodeURIComponent(request.sessionId)}`;

  const socket = browserSocket(url, {
    onOpen: () => {},
    onMessage: (raw) => {
      let frame: Frame;
      try {
        frame = JSON.parse(raw) as Frame;
      } catch {
        return;
      }

      const payload = frame.payload ?? {};
      switch (frame.type) {
        case 'session.assigned': {
          const assignment = viewedAssignmentOf(payload);
          if (assignment) onAssigned?.(assignment);
          return;
        }
        case 'session.state':
          onState?.(payload);
          return;
        case 'session.ended':
          onEnded?.();
          return;
        case 'error': {
          const message = payload['message'];
          onTrouble?.(typeof message === 'string' ? message : 'de sessie kon niet geopend worden');
          return;
        }
      }
    },
    // A ticket is good once, so this cannot reconnect on its own. The console
    // owns the credential and re-mounts the frame with a fresh one.
    onClose: () => onTrouble?.('verbinding met de les verbroken'),
  });

  return {
    onAssigned: (handler) => (onAssigned = handler),
    onState: (handler) => (onState = handler),
    onEnded: (handler) => (onEnded = handler),
    onTrouble: (handler) => (onTrouble = handler),
    stop: () => socket.close(),
  };
}
